/**
 * Dashboard.jsx — Painel de Controle
 * Filtro global no topo — afeta cards, gráficos e tabela
 * v2 — integração completa com módulo de aluguéis
 */

import React, { useEffect, useState, useCallback, useMemo, memo } from "react";
import * as XLSX from "xlsx";
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import api from "../../api/api";
import { useTheme } from "../../context/ThemeContext";
import "./Dashboard.scss";

const FORMAS_PAGAMENTO = ["Dinheiro", "Cartão Crédito", "Cartão Débito", "Pix"];
const PAYMENT_BADGE_MAP = { dinheiro: "cash", cartão: "card", pix: "pix" };
const MODOS = [
  { key: "todos",    label: "Todos" },
  { key: "vendas",   label: "Vendas" },
  { key: "alugueis", label: "Aluguéis" },
];

const STATUS_ALUGUEL_LABEL = {
  reservado:    { label: "Reservado",    variant: "warning" },
  confirmado:   { label: "Confirmado",   variant: "info" },
  em_andamento: { label: "Em andamento", variant: "primary" },
  devolvido:    { label: "Devolvido",    variant: "success" },
  cancelado:    { label: "Cancelado",    variant: "danger" },
};

const formatCurrency = (v) =>
  Number(v ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const formatCurrencyShort = (v) => {
  const n = Number(v ?? 0);
  return n >= 1000 ? `R$ ${(n / 1000).toFixed(1)}k` : `R$ ${n.toFixed(0)}`;
};
const formatDateShort = (d) =>
  new Date(d).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
const formatDateFull = (d) => new Date(d + "T00:00:00").toLocaleDateString("pt-BR");
const formatDatetime = (d) => new Date(d).toLocaleDateString("pt-BR");
const getCSSVar = (n) =>
  getComputedStyle(document.documentElement).getPropertyValue(n).trim();
const ok = (r, fb) => (r.status === "fulfilled" ? r.value.data : fb);

// ─── Componentes auxiliares ───────────────────────────────────────────────────

const SkeletonCard = () => (
  <div className="stat-card stat-card--skeleton" aria-hidden="true">
    <div className="stat-card__icon-wrap"><div className="skeleton skeleton--icon" /></div>
    <div className="stat-card__body">
      <div className="skeleton skeleton--label" />
      <div className="skeleton skeleton--value" />
    </div>
  </div>
);

const ModoFilter = memo(({ modo, onChange }) => (
  <div className="modo-filter" role="group" aria-label="Filtrar por tipo">
    {MODOS.map((m) => (
      <button
        key={m.key}
        className={`modo-filter__btn${modo === m.key ? " modo-filter__btn--active" : ""}`}
        onClick={() => onChange(m.key)}
        aria-pressed={modo === m.key}
      >
        {m.label}
      </button>
    ))}
  </div>
));
ModoFilter.displayName = "ModoFilter";

const StatCard = memo(({ title, value, icon, accent, accentVariant, trend, trendUp }) => (
  <div className={`stat-card${accent ? ` stat-card--${accentVariant ?? "accent"}` : ""}`}>
    <div className="stat-card__icon-wrap">
      <span className="stat-card__icon" aria-hidden="true">{icon}</span>
    </div>
    <div className="stat-card__body">
      <p className="stat-card__label">{title}</p>
      <p className="stat-card__value">{value}</p>
      {trend !== undefined && trend !== null && (
        <p className={`stat-card__trend${trendUp ? " stat-card__trend--up" : ""}`}>{trend}</p>
      )}
    </div>
  </div>
));
StatCard.displayName = "StatCard";

const TooltipEntradas = memo(({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip" role="tooltip">
      <p className="chart-tooltip__label">{label}</p>
      <p className="chart-tooltip__value">{payload[0]?.value} un</p>
    </div>
  );
});
TooltipEntradas.displayName = "TooltipEntradas";

const TooltipReceita = memo(({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip" role="tooltip">
      <p className="chart-tooltip__label">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="chart-tooltip__value" style={{ color: p.color }}>
          {p.name}: {formatCurrency(p.value)}
        </p>
      ))}
    </div>
  );
});
TooltipReceita.displayName = "TooltipReceita";

const PaymentBadge = memo(({ forma }) => {
  const key = PAYMENT_BADGE_MAP[forma?.toLowerCase()] ?? "other";
  return <span className={`badge badge--${key}`}>{forma}</span>;
});
PaymentBadge.displayName = "PaymentBadge";

const StatusBadge = memo(({ status }) => {
  const info = STATUS_ALUGUEL_LABEL[status] ?? { label: status, variant: "other" };
  return <span className={`badge badge--status-${info.variant}`}>{info.label}</span>;
});
StatusBadge.displayName = "StatusBadge";

const GlobalFilterBar = memo(({ filtros, onChange, onSearch, onExport, hasData, isLoading, onLimpar, filtroAtivo, modo }) => (
  <div className="dashboard__global-filter">
    <div className="global-filter__bar">
      <div className="global-filter__fields">
        <div className="field-group">
          <label htmlFor="gf-dataInicio" className="field-group__label">Data início</label>
          <input id="gf-dataInicio" type="date" className="field-group__input"
            value={filtros.dataInicio} onChange={(e) => onChange("dataInicio", e.target.value)} />
        </div>
        <div className="field-group">
          <label htmlFor="gf-dataFim" className="field-group__label">Data fim</label>
          <input id="gf-dataFim" type="date" className="field-group__input"
            value={filtros.dataFim} onChange={(e) => onChange("dataFim", e.target.value)} />
        </div>
        {/* Forma de pagamento só faz sentido em vendas/todos */}
        {modo !== "alugueis" && (
          <div className="field-group">
            <label htmlFor="gf-forma" className="field-group__label">Pagamento</label>
            <select id="gf-forma" className="field-group__input"
              value={filtros.forma_pagamento} onChange={(e) => onChange("forma_pagamento", e.target.value)}>
              <option value="">Todas</option>
              {FORMAS_PAGAMENTO.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
        )}
        {/* Filtro de status só em aluguéis */}
        {modo === "alugueis" && (
          <div className="field-group">
            <label htmlFor="gf-status" className="field-group__label">Status</label>
            <select id="gf-status" className="field-group__input"
              value={filtros.status_aluguel} onChange={(e) => onChange("status_aluguel", e.target.value)}>
              <option value="">Todos</option>
              {Object.entries(STATUS_ALUGUEL_LABEL).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      <div className="global-filter__actions">
        <button className="btn-add" onClick={onSearch} disabled={isLoading} aria-busy={isLoading}>
          {isLoading ? (
            <><span className="btn__spinner" aria-hidden="true" /> Buscando...</>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
              Buscar
            </>
          )}
        </button>
        {filtroAtivo && (
          <button className="btn-limpar" onClick={onLimpar}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M18 6 6 18M6 6l12 12"/>
            </svg>
            Limpar
          </button>
        )}
        <button className="btn-edit" onClick={onExport} disabled={!hasData}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          Exportar
        </button>
      </div>
    </div>
    {filtroAtivo && (
      <div className="global-filter__ativo">
        <span className="filtro-ativo__badge">Filtro ativo</span>
        {filtros.dataInicio && (
          <span className="filtro-ativo__item">De: {formatDateFull(filtros.dataInicio)}</span>
        )}
        {filtros.dataFim && (
          <span className="filtro-ativo__item">Até: {formatDateFull(filtros.dataFim)}</span>
        )}
        {filtros.forma_pagamento && (
          <span className="filtro-ativo__item">
            <PaymentBadge forma={filtros.forma_pagamento} />
          </span>
        )}
        {filtros.status_aluguel && (
          <span className="filtro-ativo__item">
            <StatusBadge status={filtros.status_aluguel} />
          </span>
        )}
      </div>
    )}
  </div>
));
GlobalFilterBar.displayName = "GlobalFilterBar";

const ResumoBar = memo(({ resumo }) => {
  if (!resumo.length) return null;
  const totalGeral = resumo.reduce((acc, r) => acc + Number(r.total_valor), 0);
  return (
    <div className="relatorio-resumo">
      {resumo.map((r) => (
        <div key={r.forma_pagamento} className="relatorio-resumo__item">
          <PaymentBadge forma={r.forma_pagamento} />
          <span className="relatorio-resumo__vendas">
            {r.total_vendas} venda{Number(r.total_vendas) !== 1 ? "s" : ""}
          </span>
          <span className="relatorio-resumo__valor">
            {formatCurrency(r.total_valor)}
          </span>
        </div>
      ))}
      <div className="relatorio-resumo__total">
        <span>Total geral</span>
        <span>{formatCurrency(totalGeral)}</span>
      </div>
    </div>
  );
});
ResumoBar.displayName = "ResumoBar";

// ── Resumo de aluguéis por status ──────────────────────────────────────────
const ResumoAlugueisBar = memo(({ alugueis }) => {
  if (!alugueis.length) return null;

  const totalValor = alugueis.reduce((acc, a) => acc + Number(a.valor_total), 0);
  const totalPago  = alugueis.reduce((acc, a) => acc + Number(a.valor_pago),  0);
  const totalSaldo = totalValor - totalPago;

  const porStatus = alugueis.reduce((acc, a) => {
    acc[a.status] = (acc[a.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="relatorio-resumo relatorio-resumo--alugueis">
      {Object.entries(porStatus).map(([status, count]) => (
        <div key={status} className="relatorio-resumo__item">
          <StatusBadge status={status} />
          <span className="relatorio-resumo__vendas">
            {count} aluguel{count !== 1 ? "s" : ""}
          </span>
        </div>
      ))}
      <div className="relatorio-resumo__financeiro">
        <span className="relatorio-resumo__fin-item">
          Total: <strong>{formatCurrency(totalValor)}</strong>
        </span>
        <span className="relatorio-resumo__fin-sep">·</span>
        <span className="relatorio-resumo__fin-item relatorio-resumo__fin-item--pago">
          Pago: <strong>{formatCurrency(totalPago)}</strong>
        </span>
        {totalSaldo > 0 && (
          <>
            <span className="relatorio-resumo__fin-sep">·</span>
            <span className="relatorio-resumo__fin-item relatorio-resumo__fin-item--saldo">
              Saldo: <strong>{formatCurrency(totalSaldo)}</strong>
            </span>
          </>
        )}
      </div>
    </div>
  );
});
ResumoAlugueisBar.displayName = "ResumoAlugueisBar";

const SalesTable = memo(({ vendas, loading }) => {
  if (loading) return (
    <div className="table-loading">
      <div className="spinner" role="status" /><span>Carregando...</span>
    </div>
  );
  if (!vendas.length) return (
    <div className="empty-state" role="status">
      <svg className="empty-state__icon" width="36" height="36" viewBox="0 0 24 24"
        fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
        <rect x="9" y="3" width="6" height="4" rx="1"/><path d="m9 14 2 2 4-4"/>
      </svg>
      <p className="empty-state__text">Nenhuma venda encontrada.</p>
    </div>
  );
  return (
    <div className="table-wrapper" role="region">
      <table className="sales-table">
        <thead><tr>
          <th>Data</th><th>Produto</th><th>Qtd</th>
          <th>Unit.</th><th>Total</th><th>Pagamento</th>
        </tr></thead>
        <tbody>
          {vendas.map((v) => (
            <tr key={`${v.venda_id}-${v.produto_id}`}>
              <td data-label="Data">{formatDatetime(v.data_criacao)}</td>
              <td data-label="Produto" className="cell--produto">{v.produto_nome}</td>
              <td data-label="Qtd">{v.quantidade}</td>
              <td data-label="Unit.">{formatCurrency(v.preco_custo)}</td>
              <td data-label="Total" className="cell--total">{formatCurrency(v.valor_item)}</td>
              <td data-label="Pagamento"><PaymentBadge forma={v.forma_pagamento} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});
SalesTable.displayName = "SalesTable";

// ── Tabela de aluguéis ─────────────────────────────────────────────────────
const AlugueisTable = memo(({ alugueis, loading }) => {
  if (loading) return (
    <div className="table-loading">
      <div className="spinner" role="status" /><span>Carregando...</span>
    </div>
  );
  if (!alugueis.length) return (
    <div className="empty-state" role="status">
      <svg className="empty-state__icon" width="36" height="36" viewBox="0 0 24 24"
        fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
      <p className="empty-state__text">Nenhum aluguel encontrado.</p>
    </div>
  );

  return (
    <div className="table-wrapper" role="region">
      <table className="sales-table sales-table--alugueis">
        <thead><tr>
          <th>Retirada</th>
          <th>Devolução</th>
          <th>Locatário</th>
          <th>Itens</th>
          <th>Total</th>
          <th>Pago</th>
          <th>Saldo</th>
          <th>Status</th>
        </tr></thead>
        <tbody>
          {alugueis.map((a) => {
            const saldo = Number(a.valor_total) - Number(a.valor_pago);
            const itensNomes = Array.isArray(a.itens)
              ? a.itens.map((i) => `${i.produto_nome} ×${i.quantidade}`).join(", ")
              : "—";

            return (
              <tr key={a.id}>
                <td data-label="Retirada">{formatDateFull(a.data_retirada)}</td>
                <td data-label="Devolução">{formatDateFull(a.data_devolucao)}</td>
                <td data-label="Locatário" className="cell--produto">
                  {a.locatario_nome || <span className="cell--muted">Não informado</span>}
                </td>
                <td data-label="Itens" className="cell--itens" title={itensNomes}>
                  {itensNomes.length > 40 ? itensNomes.slice(0, 38) + "…" : itensNomes}
                </td>
                <td data-label="Total" className="cell--total">{formatCurrency(a.valor_total)}</td>
                <td data-label="Pago" className="cell--pago">{formatCurrency(a.valor_pago)}</td>
                <td data-label="Saldo" className={`cell--saldo${saldo > 0 ? " cell--saldo-aberto" : ""}`}>
                  {saldo > 0 ? formatCurrency(saldo) : <span className="cell--quitado">Quitado</span>}
                </td>
                <td data-label="Status"><StatusBadge status={a.status} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
});
AlugueisTable.displayName = "AlugueisTable";

const ErrorState = memo(({ message, onRetry }) => (
  <div className="error-state" role="alert">
    <svg className="error-state__icon" width="36" height="36" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="12"/>
      <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
    <p className="error-state__message">{message}</p>
    {onRetry && <button className="btn-add" onClick={onRetry}>Tentar novamente</button>}
  </div>
));
ErrorState.displayName = "ErrorState";

const FaturamentoBreakdown = memo(({ vendas, alugueis }) => (
  <div className="fat-breakdown">
    <span className="fat-breakdown__item fat-breakdown__item--vendas">
      <span className="fat-breakdown__dot" />Vendas: {formatCurrency(vendas)}
    </span>
    <span className="fat-breakdown__sep">·</span>
    <span className="fat-breakdown__item fat-breakdown__item--alugueis">
      <span className="fat-breakdown__dot" />Aluguéis: {formatCurrency(alugueis)}
    </span>
  </div>
));
FaturamentoBreakdown.displayName = "FaturamentoBreakdown";

// ─── Dashboard principal ──────────────────────────────────────────────────────

const Dashboard = () => {
  const { isDark } = useTheme();
  const [modo, setModo] = useState("todos");

  const [filtros, setFiltros] = useState({
    dataInicio: "", dataFim: "", produto_id: "", forma_pagamento: "", status_aluguel: "",
  });

  const [stats, setStats] = useState({
    totalProdutos:  0,
    totalVolumes:   0,
    entradaDiaria:  { total_entradas: 0, total_volumes: 0 },
    vendaDiaria:    { total_vendas: 0, valor_total_vendas: 0 },
    faturamento:    { receita_vendas: 0, receita_alugueis: 0, total: 0 },
    aluguelDiario:  { total_alugueis: 0, valor_recebido_hoje: 0 },
  });
  const [graficoEntradas, setGraficoEntradas] = useState([]);
  const [graficoReceita,  setGraficoReceita]  = useState([]);
  const [loadingStats,    setLoadingStats]    = useState(true);
  const [error,           setError]           = useState(null);

  // ── Estado separado para vendas e aluguéis ────────────────────────────────
  const [vendas,          setVendas]          = useState([]);
  const [resumo,          setResumo]          = useState([]);
  const [loadingVendas,   setLoadingVendas]   = useState(false);

  const [alugueis,        setAlugueis]        = useState([]);
  const [loadingAlugueis, setLoadingAlugueis] = useState(false);

  const filtroAtivo = !!(filtros.dataInicio || filtros.dataFim || filtros.forma_pagamento || filtros.status_aluguel);

  // ── Stats ──────────────────────────────────────────────────────────────────
  const fetchStats = useCallback(async (modoAtual = "todos", filtrosAtivos = {}) => {
    setLoadingStats(true);
    setError(null);
    try {
      const filtroParams = Object.fromEntries(
        Object.entries(filtrosAtivos).filter(([_, v]) => v !== "")
      );
      const params = { modo: modoAtual, ...filtroParams };

      const [prodR, volR, entR, venR, entGraf, faturR, alugR, recGraf] =
        await Promise.allSettled([
          api.get("/dashboard/total-produtos"),
          api.get("/dashboard/total-volumes"),
          api.get("/dashboard/entrada-diaria",        { params: filtroParams }),
          api.get("/dashboard/venda-diaria",          { params: filtroParams }),
          api.get("/dashboard/entradas-ultimos-dias"),
          api.get("/dashboard/faturamento-diario",    { params }),
          api.get("/dashboard/aluguel-diario",        { params: filtroParams }),
          api.get("/dashboard/receita-ultimos-dias",  { params }),
        ]);

      if ([prodR, volR, entR, venR, entGraf].some((r) => r.status === "rejected")) {
        setError("Não foi possível carregar os dados. Tente novamente.");
        return;
      }

      const venData   = ok(venR,  { total_vendas: 0, valor_total_vendas: 0 });
      const entData   = ok(entR,  { total_entradas: 0, total_volumes: 0 });
      const faturData = ok(faturR, {
        receita_vendas:   Number(venData.valor_total_vendas ?? 0),
        receita_alugueis: 0,
        total:            Number(venData.valor_total_vendas ?? 0),
      });
      const alugData = ok(alugR, { total_alugueis: 0, valor_recebido_hoje: 0 });

      setStats({
        totalProdutos: Number(ok(prodR, { total_produtos: 0 }).total_produtos),
        totalVolumes:  Number(ok(volR,  { total_volumes:  0 }).total_volumes),
        entradaDiaria: {
          total_entradas: Number(entData.total_entradas),
          total_volumes:  Number(entData.total_volumes),
        },
        vendaDiaria: {
          total_vendas:       Number(venData.total_vendas),
          valor_total_vendas: Number(venData.valor_total_vendas),
        },
        faturamento: {
          receita_vendas:   Number(faturData.receita_vendas),
          receita_alugueis: Number(faturData.receita_alugueis),
          total:            Number(faturData.total),
        },
        aluguelDiario: {
          total_alugueis:      Number(alugData.total_alugueis),
          valor_recebido_hoje: Number(alugData.valor_recebido_hoje),
        },
      });

      setGraficoEntradas(
        ok(entGraf, []).map((e) => ({
          data:       formatDateShort(e.data_entrada),
          quantidade: Number(e.quantidade),
        }))
      );

      if (recGraf.status === "fulfilled") {
        setGraficoReceita(
          recGraf.value.data.map((d) => ({
            data:     formatDateShort(d.data),
            vendas:   Number(d.vendas),
            alugueis: Number(d.alugueis),
          }))
        );
      } else {
        try {
          const r = await api.get("/dashboard/vendas-ultimos-dias");
          setGraficoReceita(r.data.map((v) => ({
            data:     formatDateShort(v.data_criacao),
            vendas:   Number(v.valor_total),
            alugueis: 0,
          })));
        } catch { setGraficoReceita([]); }
      }
    } catch (err) {
      console.error("[Dashboard]", err);
      setError("Não foi possível carregar os dados. Tente novamente.");
    } finally {
      setLoadingStats(false);
    }
  }, []);

  // ── Buscar vendas ──────────────────────────────────────────────────────────
  const buscarVendas = useCallback(async (filtrosAtivos = filtros) => {
    setLoadingVendas(true);
    try {
      const params = Object.fromEntries(
        Object.entries(filtrosAtivos).filter(([_, v]) => v !== "" && _ !== "status_aluguel")
      );
      const [resVendas, resResumo] = await Promise.all([
        api.get("/dashboard/relatorio-vendas", { params }),
        api.get("/dashboard/relatorio-resumo", { params }),
      ]);
      setVendas(resVendas.data);
      setResumo(resResumo.data);
    } catch (err) {
      console.error("[Dashboard] Relatório vendas:", err);
    } finally {
      setLoadingVendas(false);
    }
  }, [filtros]);

  // ── Buscar aluguéis ────────────────────────────────────────────────────────
  const buscarAlugueis = useCallback(async (filtrosAtivos = filtros) => {
    setLoadingAlugueis(true);
    try {
      // Monta params compatíveis com GET /aluguel
      const params = {};
      if (filtrosAtivos.dataInicio)    params.inicio          = filtrosAtivos.dataInicio;
      if (filtrosAtivos.dataFim)       params.fim             = filtrosAtivos.dataFim;
      if (filtrosAtivos.status_aluguel) params.status         = filtrosAtivos.status_aluguel;

      const res = await api.get("/aluguel", { params });
      setAlugueis(res.data);
    } catch (err) {
      console.error("[Dashboard] Relatório aluguéis:", err);
      setAlugueis([]);
    } finally {
      setLoadingAlugueis(false);
    }
  }, [filtros]);

  // Carga inicial
  useEffect(() => {
    fetchStats("todos", {});
    buscarVendas({});
    buscarAlugueis({});
  }, []); // eslint-disable-line

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleModo = useCallback((novoModo) => {
    setModo(novoModo);
    fetchStats(novoModo, filtros);
  }, [fetchStats, filtros]);

  const handleFiltroChange = useCallback(
    (key, value) => setFiltros((p) => ({ ...p, [key]: value })), []
  );

  const handleBuscar = useCallback(() => {
    fetchStats(modo, filtros);
    if (modo !== "alugueis") buscarVendas(filtros);
    if (modo !== "vendas")   buscarAlugueis(filtros);
  }, [fetchStats, modo, filtros, buscarVendas, buscarAlugueis]);

  const handleLimpar = useCallback(() => {
    const limpo = { dataInicio: "", dataFim: "", produto_id: "", forma_pagamento: "", status_aluguel: "" };
    setFiltros(limpo);
    fetchStats(modo, {});
    buscarVendas({});
    buscarAlugueis({});
  }, [fetchStats, modo, buscarVendas, buscarAlugueis]);

  // ── Exportar Excel — adapta ao modo ───────────────────────────────────────
  const exportarExcel = useCallback(() => {
    if (modo === "alugueis") {
      if (!alugueis.length) return;
      const ws = XLSX.utils.json_to_sheet(alugueis.map((a) => ({
        "Retirada":     formatDateFull(a.data_retirada),
        "Devolução":    formatDateFull(a.data_devolucao),
        "Locatário":    a.locatario_nome || "Não informado",
        "CPF":          a.locatario_cpf  || "",
        "Telefone":     a.locatario_tel  || "",
        "Itens":        Array.isArray(a.itens)
                          ? a.itens.map((i) => `${i.produto_nome} ×${i.quantidade}`).join("; ")
                          : "",
        "Total":        formatCurrency(a.valor_total),
        "Pago":         formatCurrency(a.valor_pago),
        "Saldo":        formatCurrency(Number(a.valor_total) - Number(a.valor_pago)),
        "Status":       STATUS_ALUGUEL_LABEL[a.status]?.label ?? a.status,
        "Observações":  a.observacoes || "",
      })));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Aluguéis");
      XLSX.writeFile(wb, `Relatorio_Alugueis_${new Date().toLocaleDateString("pt-BR").replace(/\//g, "-")}.xlsx`);
    } else {
      if (!vendas.length) return;
      const ws = XLSX.utils.json_to_sheet(vendas.map((v) => ({
        Data:            formatDatetime(v.data_criacao),
        Produto:         v.produto_nome,
        Quantidade:      v.quantidade,
        "Vlr. Unitário": formatCurrency(v.preco_custo),
        "Vlr. Total":    formatCurrency(v.valor_item),
        Pagamento:       v.forma_pagamento,
      })));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Relatório de Vendas");
      XLSX.writeFile(wb, `Relatorio_Vendas_${new Date().toLocaleDateString("pt-BR").replace(/\//g, "-")}.xlsx`);
    }
  }, [modo, vendas, alugueis]);

  // ── Chart helpers ──────────────────────────────────────────────────────────
  const chartColors = useMemo(() => ({
    primary:   getCSSVar("--color-primary"),
    success:   getCSSVar("--color-success"),
    warning:   "#f59e0b",
    border:    getCSSVar("--color-border"),
    textMuted: getCSSVar("--color-text-muted"),
    surface:   getCSSVar("--color-surface"),
    text:      getCSSVar("--color-text-primary"),
  }), [isDark]); // eslint-disable-line

  const tooltipStyle = useMemo(() => ({
    background:   chartColors.surface,
    color:        chartColors.text,
    border:       `1px solid ${chartColors.border}`,
    borderRadius: "10px",
    fontSize:     "13px",
    padding:      "8px 14px",
    boxShadow:    isDark ? "0 8px 32px rgba(0,0,0,0.5)" : "0 4px 20px rgba(17,24,39,0.10)",
  }), [chartColors, isDark]);

  const mostrarVendas   = modo === "todos" || modo === "vendas";
  const mostrarAlugueis = modo === "todos" || modo === "alugueis";

  // ── Dados do relatório dependendo do modo ─────────────────────────────────
  const hasExportData = modo === "alugueis" ? alugueis.length > 0 : vendas.length > 0;
  const isLoadingRelatorio = modo === "alugueis" ? loadingAlugueis : loadingVendas;

  const tituloRelatorio =
    modo === "alugueis" ? "Relatório de Aluguéis" :
    modo === "vendas"   ? "Relatório de Vendas"   :
    "Relatório de Vendas";

  const countRelatorio =
    modo === "alugueis" ? alugueis.length : vendas.length;

  if (error) return (
    <div className="dashboard">
      <ErrorState message={error} onRetry={() => fetchStats(modo, filtros)} />
    </div>
  );

  return (
    <div className="dashboard">

      {/* ── Cabeçalho ── */}
      <header className="dashboard__header">
        <div>
          <h1 className="dashboard__title">Painel de Controle</h1>
          <p className="dashboard__subtitle">Visão geral do seu negócio</p>
        </div>
        <div className="dashboard__header-right">
          <ModoFilter modo={modo} onChange={handleModo} />
          <time className="dashboard__date" dateTime={new Date().toISOString()}>
            {new Date().toLocaleDateString("pt-BR", {
              weekday: "long", day: "2-digit", month: "long", year: "numeric",
            })}
          </time>
        </div>
      </header>

      {/* ── Filtro global ── */}
      <GlobalFilterBar
        filtros={filtros}
        onChange={handleFiltroChange}
        onSearch={handleBuscar}
        onExport={exportarExcel}
        hasData={hasExportData}
        isLoading={isLoadingRelatorio || loadingStats}
        onLimpar={handleLimpar}
        filtroAtivo={filtroAtivo}
        modo={modo}
      />

      {/* ── KPIs + Gráfico receita ── */}
      <section className="dashboard__main-block" aria-label="Resumo do dia">
        <div className="stats-grid stats-grid--3rows" aria-busy={loadingStats} aria-live="polite">
          {loadingStats
            ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
            : (
              <>
                <StatCard
                  title="Produtos Ativos"
                  value={stats.totalProdutos.toLocaleString("pt-BR")}
                  icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>}
                />
                <StatCard
                  title="Volumes em Estoque"
                  value={stats.totalVolumes.toLocaleString("pt-BR")}
                  icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>}
                />
                {mostrarVendas && (
                  <StatCard
                    title={filtroAtivo ? "Vendas (período)" : "Vendas Hoje"}
                    value={formatCurrency(stats.vendaDiaria.valor_total_vendas)}
                    trend={`${stats.vendaDiaria.total_vendas} pedidos`}
                    trendUp accent accentVariant="accent"
                    icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>}
                  />
                )}
                {mostrarVendas && (
                  <StatCard
                    title="Entradas Hoje"
                    value={stats.entradaDiaria.total_entradas.toLocaleString("pt-BR")}
                    trend={`${stats.entradaDiaria.total_volumes} volumes`}
                    icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>}
                  />
                )}
                {mostrarAlugueis && (
                  <StatCard
                    title="Aluguéis Ativos"
                    value={stats.aluguelDiario.total_alugueis.toLocaleString("pt-BR")}
                    trend={
                      stats.aluguelDiario.valor_recebido_hoje > 0
                        ? `${formatCurrency(stats.aluguelDiario.valor_recebido_hoje)} recebidos hoje`
                        : "Nenhum pagamento hoje"
                    }
                    trendUp={stats.aluguelDiario.valor_recebido_hoje > 0}
                    accent accentVariant="accent-amber"
                    icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>}
                  />
                )}
                <StatCard
                  title={
                    modo === "vendas"   ? "Receita de Vendas" :
                    modo === "alugueis" ? "Receita de Aluguéis" :
                    filtroAtivo        ? "Faturamento (período)" : "Faturamento Hoje"
                  }
                  value={formatCurrency(stats.faturamento.total)}
                  trend={modo === "todos"
                    ? <FaturamentoBreakdown vendas={stats.faturamento.receita_vendas} alugueis={stats.faturamento.receita_alugueis} />
                    : null
                  }
                  trendUp={stats.faturamento.total > 0}
                  accent accentVariant="accent-green"
                  icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>}
                />
                {!mostrarVendas   && <div className="stat-card stat-card--ghost" aria-hidden="true" />}
                {!mostrarAlugueis && <div className="stat-card stat-card--ghost" aria-hidden="true" />}
              </>
            )
          }
        </div>

        <div className="chart-receita" aria-label="Gráfico de receita dos últimos dias">
          <div className="chart-receita__header">
            <h3 className="chart-card__title">
              {filtroAtivo ? "Receita — período filtrado" : "Receita — últimos 10 dias"}
            </h3>
            {modo === "todos" && (
              <div className="chart-legend">
                <span className="chart-legend__item chart-legend__item--vendas">Vendas</span>
                <span className="chart-legend__item chart-legend__item--alugueis">Aluguéis</span>
              </div>
            )}
          </div>
          <div className="chart-receita__body">
            {loadingStats
              ? <div className="chart-skeleton"><div className="skeleton skeleton--chart" /></div>
              : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={graficoReceita} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gradVendas" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={chartColors.success} stopOpacity={isDark ? 0.3 : 0.2} />
                        <stop offset="100%" stopColor={chartColors.success} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gradAlugueis" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={chartColors.warning} stopOpacity={isDark ? 0.3 : 0.2} />
                        <stop offset="100%" stopColor={chartColors.warning} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartColors.border} vertical={false} />
                    <XAxis dataKey="data" stroke={chartColors.textMuted} tick={{ fontSize: 11, fill: chartColors.textMuted }} axisLine={false} tickLine={false} />
                    <YAxis stroke={chartColors.textMuted} tick={{ fontSize: 10, fill: chartColors.textMuted }} axisLine={false} tickLine={false} width={68} tickFormatter={formatCurrencyShort} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: chartColors.border, strokeWidth: 1 }} content={<TooltipReceita />} />
                    {mostrarVendas && (
                      <Area type="monotone" dataKey="vendas" name="Vendas" stroke={chartColors.success} strokeWidth={2} fill="url(#gradVendas)" dot={false} activeDot={{ r: 4, fill: chartColors.success, stroke: chartColors.surface, strokeWidth: 2 }} />
                    )}
                    {mostrarAlugueis && (
                      <Area type="monotone" dataKey="alugueis" name="Aluguéis" stroke={chartColors.warning} strokeWidth={2} fill="url(#gradAlugueis)" dot={false} activeDot={{ r: 4, fill: chartColors.warning, stroke: chartColors.surface, strokeWidth: 2 }} />
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              )
            }
          </div>
        </div>
      </section>

      {/* ── Entradas + Relatório ── */}
      <section className="dashboard__secondary-block" aria-label="Entradas e relatório">
        <div className="chart-entradas" aria-label="Entradas dos últimos dias">
          <h3 className="chart-card__title">Entradas — últimos dias</h3>
          <div className="chart-entradas__body">
            {loadingStats
              ? <div className="chart-skeleton"><div className="skeleton skeleton--chart" /></div>
              : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={graficoEntradas} barSize={20} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartColors.border} vertical={false} />
                    <XAxis dataKey="data" stroke={chartColors.textMuted} tick={{ fontSize: 11, fill: chartColors.textMuted }} axisLine={false} tickLine={false} />
                    <YAxis stroke={chartColors.textMuted} tick={{ fontSize: 11, fill: chartColors.textMuted }} axisLine={false} tickLine={false} width={28} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: isDark ? "rgba(255,255,255,0.04)" : "rgba(26,86,219,0.05)" }} content={<TooltipEntradas />} />
                    <Bar dataKey="quantidade" fill={chartColors.primary} radius={[5, 5, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )
            }
          </div>
        </div>

        <section className="report-section" aria-label={tituloRelatorio}>
          <div className="report-section__header">
            <div>
              <h2 className="report-section__title">{tituloRelatorio}</h2>
              {countRelatorio > 0 && (
                <span className="report-section__count">
                  {countRelatorio} registro{countRelatorio !== 1 ? "s" : ""}
                </span>
              )}
            </div>
          </div>

          {/* Resumo condicional por modo */}
          {modo !== "alugueis" && <ResumoBar resumo={resumo} />}
          {modo === "alugueis" && <ResumoAlugueisBar alugueis={alugueis} />}

          {/* Tabela condicional por modo */}
          {modo !== "alugueis"
            ? <SalesTable   vendas={vendas}     loading={loadingVendas} />
            : <AlugueisTable alugueis={alugueis} loading={loadingAlugueis} />
          }
        </section>
      </section>

    </div>
  );
};

export default Dashboard;